import { PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import {
  AMM_POOL_SEED,
  AUTH_NONCE_SEED,
  BACKER_FEE_SEED,
  BACKER_FEE_SHARE_CONFIG_SEED,
  BACKER_SHARE_MINT_SEED,
  BC_QUOTE_SEED,
  BC_SEED,
  BOOST_RECEIPT_SEED,
  BOOST_VAULT_SEED,
  CHALLENGE_STAKE_SEED,
  CHALLENGE_VAULT_SEED,
  CLAIM_POOL_VAULT_SEED,
  CLAIM_RECEIPT_SEED,
  CLAIM_RIGHTS_MINT_SEED,
  CREATOR_PROFILE_SEED,
  DRIP_POOL_VAULT_SEED,
  EVENT_AUTHORITY_SEED,
  GIFT_ADMIN_SEED,
  GIFT_CONFIG_SEED,
  GIFT_DEPOSIT_SEED,
  GIFT_SEED,
  HANDLE_VAULT_SEED,
  MARKET_CONFIG_SEED,
  MARKET_GROUP_MINT_SEED,
  MARKET_SEED,
  METADATA_SEED,
  MINT_AUTHORITY_SEED,
  OUTCOME_MINT_SEED,
  OUTCOME_SEED,
  PLATFORM_CONFIG_SEED,
  PLATFORM_FEE_VAULT_SEED,
  PRIZE_POOL_SEED,
  PRIZE_SEED,
  PROPOSAL_SEED,
  PROPOSAL_STAKE_SEED,
  PROPOSAL_VAULT_SEED,
  REFERRAL_LINK_SEED,
  REVENUE_SHARE_POOL_SEED,
  REVENUE_SHARE_SEED,
  ROSTER_ENTRY_SEED,
  THESIS_SEED,
  VAULT_SEED,
} from '../constants/seeds.js';

/**
 * Encode a u16 value as 2-byte little-endian buffer for PDA seed derivation.
 *
 * @param value - Unsigned 16-bit integer
 * @returns 2-byte Buffer in little-endian order
 */
function u16LeBuffer(value: number): Buffer {
  const buf = Buffer.alloc(2);
  buf.writeUInt16LE(value, 0);
  return buf;
}

/**
 * Encode a u8 value as a 1-byte buffer for PDA seed derivation.
 *
 * The Rust contract derives outcome-keyed PDAs with `&[outcome_index]` —
 * a single byte. The TS mirror MUST match (`u8`, not `u16`); the previous
 * `u16LeBuffer(outcomeIndex)` produced 2-byte seeds and yielded PDAs that
 * don't exist on chain (broken trade-form on-chain reads, broken outcome
 * fetches).
 *
 * @param value - Unsigned 8-bit integer (0..=255)
 * @returns 1-byte Buffer
 */
function u8Buffer(value: number): Buffer {
  if (value < 0 || value > 255 || !Number.isInteger(value)) {
    throw new RangeError(`u8 out of range: ${value}`);
  }
  const buf = Buffer.alloc(1);
  buf.writeUInt8(value, 0);
  return buf;
}

/**
 * Encode a u32 value as 4-byte little-endian buffer for PDA seed derivation.
 *
 * @param value - Unsigned 32-bit integer
 * @returns 4-byte Buffer in little-endian order
 */
function u32LeBuffer(value: number): Buffer {
  const buf = Buffer.alloc(4);
  buf.writeUInt32LE(value, 0);
  return buf;
}

/**
 * Encode a u64 (BN) value as 8-byte little-endian buffer for PDA seed derivation.
 * Matches Rust `market_nonce.to_le_bytes()`.
 */
function u64LeBuffer(value: BN): Buffer {
  return value.toArrayLike(Buffer, 'le', 8);
}

/**
 * Derive Market PDA. Seeds: `[b"market", creator[32], market_nonce_le[8]]`.
 *
 * Mirrors the v2 opinion program's `Market` account at
 * `contracts/markets/programs/markets/src/instructions/creation/init_outcome.rs:279`:
 *   `seeds = [b"market", market.creator.as_ref(), &market.market_nonce.to_le_bytes()]`
 *
 * @param creator - Wallet pubkey of the user creating the market (on-chain `creator`)
 * @param marketNonce - u64 nonce chosen by the creator (typically a fresh random 8 bytes)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveMarketPda(
  creator: PublicKey,
  marketNonce: BN,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [MARKET_SEED, creator.toBuffer(), u64LeBuffer(marketNonce)],
    programId,
  );
}

/**
 * Derive Outcome PDA. Seeds: `[b"outcome", market[32], outcome_index[1]]`.
 *
 * The Rust contract uses a single-byte `u8` for `outcome_index`
 * (`contracts/markets/programs/markets/src/instructions/creation/init_outcome.rs`
 * — `seeds = [b"outcome", market.key().as_ref(), &[outcome_index]]`). The
 * `u8Buffer` helper produces a 1-byte seed; using `u16LeBuffer` here
 * yields a PDA that does NOT exist on chain and breaks every on-chain
 * outcome fetch (`fetchOutcome`, `useOnchainTradeData`, the buy/sell
 * estimate path).
 *
 * @param market - Market account public key
 * @param outcomeIndex - Outcome index (u8, 0..=255)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveOutcomePda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [OUTCOME_SEED, market.toBuffer(), u8Buffer(outcomeIndex)],
    programId,
  );
}

/**
 * Derive Outcome Mint PDA. Seeds: [b"outcome_mint", market[32], outcome_index_le[2]].
 *
 * @param market - Market account public key
 * @param outcomeIndex - Outcome index (u16)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveOutcomeMintPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [OUTCOME_MINT_SEED, market.toBuffer(), u16LeBuffer(outcomeIndex)],
    programId,
  );
}

/**
 * Derive Mint Authority PDA. Seeds: [b"mint_authority", market[32]].
 *
 * @param market - Market account public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveMintAuthorityPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([MINT_AUTHORITY_SEED, market.toBuffer()], programId);
}

/**
 * Derive Market Group Mint PDA. Seeds: [b"market_group_mint", market[32]].
 *
 * @param market - Market account public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveMarketGroupMintPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([MARKET_GROUP_MINT_SEED, market.toBuffer()], programId);
}

/**
 * Derive AMM Pool PDA. Seeds: [b"amm_pool", market[32], outcome_index_le[2]].
 *
 * @param market - Market account public key
 * @param outcomeIndex - Outcome index (u16)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveAmmPoolPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [AMM_POOL_SEED, market.toBuffer(), u16LeBuffer(outcomeIndex)],
    programId,
  );
}

/**
 * Derive Prize Pool PDA. Seeds: [b"prize_pool", market[32]].
 *
 * @param market - Market account public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function derivePrizePoolPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([PRIZE_POOL_SEED, market.toBuffer()], programId);
}

/**
 * Derive Claim Pool Vault PDA. Seeds: [b"claim_pool_vault", market[32]].
 *
 * @param market - Market account public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveClaimPoolVaultPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([CLAIM_POOL_VAULT_SEED, market.toBuffer()], programId);
}

/**
 * Derive Drip Pool Vault PDA. Seeds: [b"drip_pool_vault", market[32]].
 *
 * @param market - Market account public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveDripPoolVaultPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([DRIP_POOL_VAULT_SEED, market.toBuffer()], programId);
}

/**
 * Derive Boost Vault PDA. Seeds: [b"boost_vault", market[32]].
 *
 * @param market - Market account public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveBoostVaultPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([BOOST_VAULT_SEED, market.toBuffer()], programId);
}

/**
 * Derive Challenge Vault PDA. Seeds: [b"challenge_vault", market[32]].
 *
 * @param market - Market account public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveChallengeVaultPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([CHALLENGE_VAULT_SEED, market.toBuffer()], programId);
}

/**
 * Derive Challenge Stake PDA. Seeds: [b"challenge_stake", market[32], staker[32], outcome_index_le[2]].
 *
 * @param market - Market account public key
 * @param staker - Staker wallet public key
 * @param outcomeIndex - Outcome index (u16)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveChallengeStakePda(
  market: PublicKey,
  staker: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [CHALLENGE_STAKE_SEED, market.toBuffer(), staker.toBuffer(), u16LeBuffer(outcomeIndex)],
    programId,
  );
}

/**
 * Derive Roster Entry PDA. Seeds: [b"roster_entry", market[32], outcome_index_le[2]].
 *
 * @param market - Market account public key
 * @param outcomeIndex - Outcome index (u16)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveRosterEntryPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [ROSTER_ENTRY_SEED, market.toBuffer(), u16LeBuffer(outcomeIndex)],
    programId,
  );
}

/**
 * Derive Proposal PDA. Seeds: [b"proposal", proposal_id[32]].
 *
 * @param proposalId - SHA-256 hash of the proposal title (32 bytes)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveProposalPda(
  proposalId: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([PROPOSAL_SEED, proposalId], programId);
}

/**
 * Derive Proposal Stake PDA. Seeds: [b"proposal_stake", proposal_id[32], staker[32], outcome_index_le[2]].
 *
 * @param proposalId - SHA-256 hash of the proposal title (32 bytes)
 * @param staker - Staker wallet public key
 * @param outcomeIndex - Outcome index (u16)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveProposalStakePda(
  proposalId: Uint8Array,
  staker: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PROPOSAL_STAKE_SEED, proposalId, staker.toBuffer(), u16LeBuffer(outcomeIndex)],
    programId,
  );
}

/**
 * Derive Proposal Vault PDA. Seeds: [b"proposal_vault", proposal_id[32]].
 *
 * @param proposalId - SHA-256 hash of the proposal title (32 bytes)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveProposalVaultPda(
  proposalId: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([PROPOSAL_VAULT_SEED, proposalId], programId);
}

/**
 * Derive Boost Receipt PDA. Seeds: [b"boost_receipt", market[32], booster[32]].
 *
 * @param market - Market account public key
 * @param booster - Booster wallet public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveBoostReceiptPda(
  market: PublicKey,
  booster: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [BOOST_RECEIPT_SEED, market.toBuffer(), booster.toBuffer()],
    programId,
  );
}

/**
 * Derive Claim Receipt PDA. Seeds: [b"claim_receipt", market[32], user[32]].
 *
 * @param market - Market account public key
 * @param user - User wallet public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveClaimReceiptPda(
  market: PublicKey,
  user: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [CLAIM_RECEIPT_SEED, market.toBuffer(), user.toBuffer()],
    programId,
  );
}

/**
 * Derive Creator Profile PDA. Seeds: [b"creator_profile", creator_wallet[32]].
 *
 * @param creatorWallet - Creator wallet public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveCreatorProfilePda(
  creatorWallet: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [CREATOR_PROFILE_SEED, creatorWallet.toBuffer()],
    programId,
  );
}

/**
 * Derive Referral Link PDA. Seeds: [b"referral_link", referee[32]].
 *
 * @param referee - Referee wallet public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveReferralLinkPda(
  referee: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([REFERRAL_LINK_SEED, referee.toBuffer()], programId);
}

/**
 * Derive Revenue Share Pool PDA (singleton). Seeds: [b"revenue_share_pool"].
 *
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveRevenueSharePoolPda(
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([REVENUE_SHARE_POOL_SEED], programId);
}

/**
 * Derive Revenue Share Recipient PDA. Seeds: [b"revenue_share", recipient_key[32]].
 *
 * @param recipientKey - Recipient wallet public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveRevenueShareRecipientPda(
  recipientKey: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([REVENUE_SHARE_SEED, recipientKey.toBuffer()], programId);
}

/**
 * Derive Handle Vault PDA. Seeds: [b"handle_vault", handle_hash[32], cycle_index_le[2]].
 *
 * @param handleHash - SHA-256 hash of the social handle (32 bytes)
 * @param cycleIndex - Vault cycle index (u16). Increments after each claim. Defaults to 0.
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveHandleVaultPda(
  handleHash: Uint8Array,
  cycleIndex: number = 0,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [HANDLE_VAULT_SEED, handleHash, u16LeBuffer(cycleIndex)],
    programId,
  );
}

/**
 * Derive Gift Deposit PDA. LEGACY — only used to find historical
 * deposits during the migration window. New gifting goes through
 * `deriveGiftPda` / `deriveGiftConfigPda` / `deriveGiftAdminPda` below.
 */
export function deriveGiftDepositPda(
  vault: PublicKey,
  sender: PublicKey,
  sequenceNumber: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [GIFT_DEPOSIT_SEED, vault.toBuffer(), sender.toBuffer(), u32LeBuffer(sequenceNumber)],
    programId,
  );
}

/**
 * Derive GiftConfig PDA.
 * Seeds: [b"gift_config", deployer_authority[32], label[32]].
 *
 * @param deployerAuthority - Pubkey of the deployer that initialised the config.
 * @param label - 32-byte ASCII label (right-padded with zeros if shorter).
 */
/**
 * Shared derivation for the `[seed, deployer_authority[32], label[32]]`
 * config-account PDAs (GiftConfig, MarketConfig, BackerFeeShareConfig). The
 * Rust side fixes `label` at 32 bytes, so all three guard identically.
 */
function deriveLabeledConfigPda(
  fnName: string,
  seed: Uint8Array,
  deployerAuthority: PublicKey,
  label: Uint8Array,
  programId: PublicKey,
): [PublicKey, number] {
  if (label.length !== 32) {
    throw new Error(`${fnName}: label must be exactly 32 bytes, got ${label.length}`);
  }
  return PublicKey.findProgramAddressSync([seed, deployerAuthority.toBuffer(), label], programId);
}

export function deriveGiftConfigPda(
  deployerAuthority: PublicKey,
  label: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return deriveLabeledConfigPda(
    'deriveGiftConfigPda',
    GIFT_CONFIG_SEED,
    deployerAuthority,
    label,
    programId,
  );
}

/**
 * MarketConfig PDA. Seeds: `[b"config", deployer_authority[32], label[32]]`.
 * Per `admin/init_market_config.rs` (`seeds = [b"config", ...]`).
 */
export function deriveMarketConfigPda(
  deployerAuthority: PublicKey,
  label: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return deriveLabeledConfigPda(
    'deriveMarketConfigPda',
    MARKET_CONFIG_SEED,
    deployerAuthority,
    label,
    programId,
  );
}

/**
 * BackerFeeShareConfig PDA. Seeds: `[b"backer_fee_share", deployer_authority[32], label[32]]`.
 * Per `admin/init_backer_fee_share_config.rs`.
 */
export function deriveBackerFeeShareConfigPda(
  deployerAuthority: PublicKey,
  label: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return deriveLabeledConfigPda(
    'deriveBackerFeeShareConfigPda',
    BACKER_FEE_SHARE_CONFIG_SEED,
    deployerAuthority,
    label,
    programId,
  );
}

/**
 * Derive GiftAdmin PDA — the on-chain authority for the source treasury
 * ATA holding gift tokens. Seeds: [b"gift_admin", gift_config[32]].
 */
export function deriveGiftAdminPda(
  giftConfig: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([GIFT_ADMIN_SEED, giftConfig.toBuffer()], programId);
}

/**
 * Derive Gift PDA — one per (gift_config, mint, handler_hash).
 * Seeds: [b"gift", gift_config[32], mint[32], handler_hash[32]].
 */
export function deriveGiftPda(
  giftConfig: PublicKey,
  mint: PublicKey,
  handlerHash: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  if (handlerHash.length !== 32) {
    throw new Error(
      `deriveGiftPda: handlerHash must be exactly 32 bytes, got ${handlerHash.length}`,
    );
  }
  return PublicKey.findProgramAddressSync(
    [GIFT_SEED, giftConfig.toBuffer(), mint.toBuffer(), handlerHash],
    programId,
  );
}

/**
 * Derive Auth Nonce Tracker PDA.
 * Seeds: [b"auth_nonce", vault_pubkey[32], nonce[16]].
 *
 * Initialized as part of `claim_handle_vault` via Anchor `init` constraint, which fails on
 * collision and provides built-in replay protection.
 *
 * @param vault - Handle vault PDA
 * @param nonce - 16-byte random nonce from the ClaimAuthorizationPayload
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveAuthNonceTrackerPda(
  vault: PublicKey,
  nonce: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  if (nonce.length !== 16) {
    throw new Error(
      `deriveAuthNonceTrackerPda: nonce must be exactly 16 bytes, got ${nonce.length}`,
    );
  }
  return PublicKey.findProgramAddressSync([AUTH_NONCE_SEED, vault.toBuffer(), nonce], programId);
}

/**
 * Derive Thesis PDA. Seeds: [b"thesis", market[32], author[32], sig_first_32[32]].
 *
 * @param market - Market account public key
 * @param author - Author wallet public key
 * @param sigFirst32 - First 32 bytes of the trade signature
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveThesisPda(
  market: PublicKey,
  author: PublicKey,
  sigFirst32: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [THESIS_SEED, market.toBuffer(), author.toBuffer(), sigFirst32],
    programId,
  );
}

/**
 * Derive Platform Config PDA (singleton). Seeds: [b"platform_config"].
 *
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function derivePlatformConfigPda(
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([PLATFORM_CONFIG_SEED], programId);
}

// ─── v2 market-creation PDAs ────────────────────────────────────────────────
// Mirror contracts/markets/programs/markets/src/instructions/creation/*.rs.

/**
 * Bonding Curve PDA. Seeds: `[b"bc", market[32], outcome_index[1]]`.
 *
 * NOTE: the seed is the literal `b"bc"`, NOT `b"bonding_curve"` — the Rust
 * source at `init_outcome.rs:301` defines `seeds = [b"bc", market.key().as_ref(), &[outcome_index]]`.
 */
export function deriveBondingCurvePda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [BC_SEED, market.toBuffer(), u8Buffer(outcomeIndex)],
    programId,
  );
}

/**
 * BC Quote Vault PDA. Seeds: `[b"vault", b"bc_quote", market[32], outcome_index[1]]`.
 * Per `init_outcome.rs:60`.
 */
export function deriveBcQuoteVaultPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [VAULT_SEED, BC_QUOTE_SEED, market.toBuffer(), u8Buffer(outcomeIndex)],
    programId,
  );
}

/**
 * Claim Rights Mint PDA. Seeds: `[b"claim_rights_mint", market[32], outcome_index[1]]`.
 * Per `init_outcome.rs:335`.
 */
export function deriveClaimRightsMintPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [CLAIM_RIGHTS_MINT_SEED, market.toBuffer(), u8Buffer(outcomeIndex)],
    programId,
  );
}

/**
 * Backer Share Mint PDA. Seeds: `[b"backer_share_mint", market[32]]`.
 * Per `create_market_shell.rs` (per-market backer-fee-share token mint).
 */
export function deriveBackerShareMintPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([BACKER_SHARE_MINT_SEED, market.toBuffer()], programId);
}

/**
 * Prize Pool Vault PDA. Seeds: `[b"vault", b"prize", market[32]]`.
 */
export function derivePrizePoolVaultPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([VAULT_SEED, PRIZE_SEED, market.toBuffer()], programId);
}

/**
 * Backer Fee Vault PDA. Seeds: `[b"vault", b"backer_fee", market[32]]`.
 */
export function deriveBackerFeeVaultPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [VAULT_SEED, BACKER_FEE_SEED, market.toBuffer()],
    programId,
  );
}

/**
 * Platform Fee Vault PDA. Seeds: `[b"platform_fee_vault", market_config[32]]`.
 */
export function derivePlatformFeeVaultPda(
  marketConfig: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PLATFORM_FEE_VAULT_SEED, marketConfig.toBuffer()],
    programId,
  );
}

/**
 * Anchor `#[event_cpi]` event-authority PDA. Seeds: `[b"__event_authority"]`.
 * Owned by the program itself; Anchor adds this account automatically when
 * the IDL is loaded via `Program`, but exposed here for callers that bypass
 * the typed methods API.
 */
export function deriveEventAuthorityPda(
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([EVENT_AUTHORITY_SEED], programId);
}

/** MPL Token Metadata program id (mainnet + devnet — same on both clusters). */
const MPL_TOKEN_METADATA_PROGRAM_ID: PublicKey = new PublicKey(
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s',
);

/**
 * Token Metadata PDA — the Metaplex-owned account holding name/symbol/uri for
 * a given SPL mint. Seeds (in MPL_TOKEN_METADATA's address space):
 *   `[b"metadata", mpl_token_metadata_program_id[32], mint[32]]`
 */
export function deriveTokenMetadataPda(mint: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [METADATA_SEED, MPL_TOKEN_METADATA_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    MPL_TOKEN_METADATA_PROGRAM_ID,
  );
}
